import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { PageTitle } from '@/components/custom/PageTitle'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { RootState } from '@/redux'
import { DashboardsTable } from './components/DashboardsTable'
import { Plus, LayoutDashboard } from 'lucide-react'
import { ButtonVariant } from '@/constants/button/button'

/**
 * DashboardList — the /dashboards page.
 *
 * Per TDD §3d.1.A (amended 2026-04-24):
 * - "New Dashboard" navigates directly to /dashboards/new — no modal, no name
 *   prompt, no navState payload.
 * - CreateOrUpdateDashboardModal is NOT rendered here. It is retained in
 *   DashboardsTable for the rename affordance only (⋮ menu → Rename).
 */
export const DashboardList = () => {
  const navigate = useNavigate()
  const { dashboards, isLoading } = useSelector((state: RootState) => state.dashboards)

  return (
    <div className="protectedPageLayout">
      <PageTitle
        title="Dashboards"
        buttonText="New Dashboard"
        onButtonClick={() => navigate('/dashboards/new')}
        buttonIcon={<Plus />}
      />

      {isLoading ? (
        <div
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 w-full"
          aria-busy="true"
          role="status"
          aria-label="Loading dashboards"
        >
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-40 rounded-lg" />
          ))}
        </div>
      ) : dashboards.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center w-full h-full">
            <div className="w-8 h-8 rounded-lg bg-accent-background flex items-center justify-center flex-shrink-0">
              <LayoutDashboard className="text-accent" size={20} />
            </div>
            <h2 className="text-lg font-semibold text-primary mb-2">No Dashboards Yet</h2>
            <p className="text-sm text-secondary max-w-sm">
              You haven't created any dashboards yet.
            </p>
            <p className="text-sm text-secondary mb-6">
              Create your first dashboard and let AI help you build it from your connected data.
            </p>
            <Button
              variant={ButtonVariant.DEFAULT}
              onClick={() => navigate('/dashboards/new')}
              className="font-semibold gap-2"
            >
              <Plus />
              New Dashboard
            </Button>
          </div>
      ) : (
        <DashboardsTable
          dashboards={dashboards}
          onNewDashboard={() => navigate('/dashboards/new')}
        />
      )}
    </div>
  )
}

export default DashboardList
